import React, { useEffect, useState } from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import Styles from "./styles.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    setCabinetPartnersCardEditModalStatus,
    setPartnersCardSettings
} from "../../../store/cabinet/action";
import { toast } from "react-toastify";

const initialFormData = {
    name: "",
    card_email: "",
    telegram: "",
};

const getErrorSource = (formErr) => {
    return (
        formErr?.response?.data?.errors ||
        formErr?.data?.errors ||
        formErr?.errors ||
        {}
    );
};

const getGeneralError = (formErr) => {
    return (
        formErr?.response?.data?.message ||
        formErr?.data?.message ||
        formErr?.message ||
        ""
    );
};

const getFieldError = (formErr, fieldName) => {
    const errors = getErrorSource(formErr);
    const fieldError = errors?.[fieldName];

    if (!fieldError) return "";

    if (Array.isArray(fieldError)) {
        return fieldError[0] || "";
    }

    return String(fieldError);
};

const CabinetPartnersCardEditModal = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const modalStatus = useSelector(store => store.cabinet.cabinetPartnersCardEditModalStatus);
    const partnersCard = useSelector(store => store.cabinet.partnersCard);
    const partnersCardSettingsStatusStarted = useSelector(store => store.cabinet.partnersCardSettingsStatusStarted);
    const formErr = useSelector(store => store.cabinet.partnersCardSettingsStatusErr);

    const [formData, setFormData] = useState(initialFormData);
    const [hiddenErrors, setHiddenErrors] = useState({});
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const closeModal = () => {
        if (partnersCardSettingsStatusStarted) return;

        dispatch(setCabinetPartnersCardEditModalStatus(false));
    };

    const inputChangeHandler = (event) => {
        const { name, value } = event.target;

        setFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));

        setHiddenErrors((prevState) => ({
            ...prevState,
            [name]: true,
        }));
    };

    const successHandler = () => {
        toast(t("Ваша визитка успешно обновлена!"));
        closeModal();
    };

    const saveHandler = () => {
        if (partnersCardSettingsStatusStarted) return;

        setHasSubmitted(true);
        setHiddenErrors({});

        const data = {
            name: formData.name.trim() || null,
            card_email: formData.card_email.trim() || null,
            telegram: formData.telegram.trim() || null,
        };

        dispatch(setPartnersCardSettings(data, successHandler));
    };

    const hasFieldError = (fieldName) => {
        if (!hasSubmitted || partnersCardSettingsStatusStarted) return false;

        return !!getFieldError(formErr, fieldName) && !hiddenErrors[fieldName];
    };

    const renderFieldError = (fieldName) => {
        if (!hasSubmitted || partnersCardSettingsStatusStarted) return null;

        const error = getFieldError(formErr, fieldName);

        if (!error || hiddenErrors[fieldName]) return null;

        return (
            <div className={Styles.formError}>
                {t(error)}
            </div>
        );
    };

    const renderGeneralError = () => {
        if (!hasSubmitted || partnersCardSettingsStatusStarted) return null;

        const generalError = getGeneralError(formErr);
        const errors = getErrorSource(formErr);
        const hasFieldErrors = Object.keys(errors || {}).length > 0;

        if (!generalError || hasFieldErrors) return null;

        return (
            <div className={Styles.generalError}>
                {t(generalError)}
            </div>
        );
    };

    useEffect(() => {
        if (!modalStatus) {
            setFormData(initialFormData);
            setHiddenErrors({});
            setHasSubmitted(false);
            return;
        }

        setFormData({
            name: partnersCard?.name ?? "",
            card_email: partnersCard?.card_email ?? "",
            telegram: partnersCard?.telegram ?? "",
        });

        setHiddenErrors({});
        setHasSubmitted(false);
    }, [modalStatus, partnersCard]);

    return (
        <Popup
            open={!!modalStatus}
            modal
            nested
            closeOnDocumentClick={!partnersCardSettingsStatusStarted}
            onClose={closeModal}
            className="cabinetPartnersCardEditModal"
        >
            <div className={Styles.modalContainer}>
                <div
                    className={`${Styles.modalCloseBtn} ${partnersCardSettingsStatusStarted ? Styles.modalCloseBtnDisabled : ""}`}
                    onClick={closeModal}
                >
                    <div />
                    <div />
                </div>

                <div className={Styles.modalContentWrap}>
                    <div className={Styles.titleRow}>
                        <div className={Styles.titleIcon}>
                            {/* icon here */}
                        </div>

                        <div className={Styles.titleTextWrap}>
                            <div className={Styles.title}>
                                {t("Редактировать визитку")}
                            </div>

                            <div className={Styles.note}>
                                {t("Обновите данные, которые будут отображаться в партнёрской визитке")}
                            </div>
                        </div>
                    </div>

                    {renderGeneralError()}

                    <div className={Styles.formWrap}>
                        <div className={Styles.formItem}>
                            <label
                                className={Styles.formLabel}
                                htmlFor="partners-card-name"
                            >
                                {t("Имя")}
                            </label>

                            <input
                                id="partners-card-name"
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={inputChangeHandler}
                                className={`${Styles.formInput} ${hasFieldError("name") ? Styles.formInputError : ""}`}
                                placeholder={t("Введите имя")}
                                disabled={partnersCardSettingsStatusStarted}
                            />

                            {renderFieldError("name")}
                        </div>

                        <div className={Styles.formItem}>
                            <label
                                className={Styles.formLabel}
                                htmlFor="partners-card-email"
                            >
                                {t("Почта")}
                            </label>

                            <input
                                id="partners-card-email"
                                type="email"
                                name="card_email"
                                value={formData.card_email}
                                onChange={inputChangeHandler}
                                className={`${Styles.formInput} ${hasFieldError("card_email") ? Styles.formInputError : ""}`}
                                placeholder={t("Введите почту")}
                                disabled={partnersCardSettingsStatusStarted}
                            />

                            {renderFieldError("card_email")}
                        </div>

                        <div className={Styles.formItem}>
                            <label
                                className={Styles.formLabel}
                                htmlFor="partners-card-telegram"
                            >
                                {t("Телеграм")}
                            </label>

                            <input
                                id="partners-card-telegram"
                                type="text"
                                name="telegram"
                                value={formData.telegram}
                                onChange={inputChangeHandler}
                                className={`${Styles.formInput} ${hasFieldError("telegram") ? Styles.formInputError : ""}`}
                                placeholder={t("Введите телеграм")}
                                disabled={partnersCardSettingsStatusStarted}
                            />

                            {renderFieldError("telegram")}
                        </div>
                    </div>

                    <div className={Styles.btnsWrap}>
                        <button
                            type="button"
                            className={Styles.cancelBtn}
                            onClick={closeModal}
                            disabled={partnersCardSettingsStatusStarted}
                        >
                            {t("Отмена")}
                        </button>

                        <button
                            type="button"
                            className={Styles.saveBtn}
                            onClick={saveHandler}
                            disabled={partnersCardSettingsStatusStarted}
                        >
                            {partnersCardSettingsStatusStarted ? t("Сохранение") : t("Сохранить")}
                        </button>
                    </div>
                </div>
            </div>
        </Popup>
    );
};

export default CabinetPartnersCardEditModal;
